// Сертификаты РМРС/РКО на оборудование — картотека PDF. ТОЛЬКО ЧТЕНИЕ.
//
// ГЛАВНЫЙ ВОПРОС ЭКРАНА: «этот прибор с этим заводским номером одобрен, и где бумага?».
// Раздел открывает инженер, которому инспектор в порту сказал «покажите свидетельство».
// Поэтому наверху не таблица и не плитки, а ДВА ПОЛЯ ПОИСКА — модель и заводской номер, —
// а в каждой строке ссылка на PDF.
//
// ЧЕГО ЭТОТ РАЗДЕЛ НЕ ЗНАЕТ, И ПОЧЕМУ ОБ ЭТОМ НАПИСАНО НА САМОМ ВИДУ
//   1) СРОКА ДЕЙСТВИЯ НЕТ. В индексе портала его нет ни у одного файла, поэтому колонки
//      «действует до» здесь нет вовсе. CRM отвечает «такой сертификат есть», а не «он
//      действует». Пустая колонка со сроком читалась бы как «бессрочно» — это была бы ложь
//      в единственном месте, где она дороже всего.
//   2) ЭТО НЕ ВСЯ КАРТОТЕКА. Портал 55 ищет по сумме двух наборов: этих файлов из R2 и своего
//      старого набора из Nextcloud. Не найдя здесь, человек ОБЯЗАН узнать, что остаётся
//      проверить на портале, — иначе он скажет судовладельцу «сертификата нет».
//   3) СЕРИЙНИКИ ПРОЧИТАНЫ МОДЕЛЬЮ. Номер может быть пропущен или прочитан с опечаткой,
//      поэтому у поиска по номеру есть переключатель «внутри номера», а у пустого ответа —
//      объяснение, а не молчание.
// Тексты этих оговорок приходят из ядра (data_notes): наполнится источник — раздел это
// увидит сам, без правки экрана.
//
// ПРО ССЫЛКУ НА ФАЙЛ. PDF раздаёт портал 55, а не CRM (у ядра нет доступа к бакету R2).
// Портал отдаёт файл только вошедшему: без сессии портала ссылка ответит «Unauthorized»,
// и это НЕ значит «файла нет». Об этом сказано рядом со ссылками, а не в примечании внизу.
import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { useСертификат, useСертификаты, useСертыRefs } from '../api/certs'
import type {
  КарточкаСертификата, Сертификат, СледВРеестре, СправочникиСертов,
} from '../api/certs'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtText, plural, pluralN } from '../lib/format'
import {
  ПОЯСНЕНИЕ_РЕЖИМА, ПУТИ, РАЗМЕР_СТРАНИЦЫ, видСвидетельства, классВида, подписьПроблемы,
  размерФайла, серьёзнаяНеполнота,
} from '../lib/certs'
import type { ФильтрыСертов } from '../lib/certs'
// Путь к карточке свидетельства реестра берём у самого раздела реестра, а не составляем
// строкой: свой хардкод однажды разойдётся с их маршрутом, и ссылка молча приведёт в 404.
import { ПУТИ as ПУТИ_РЕЕСТРА } from '../lib/p55'
import { Охват, Пейджер } from './RsVessels'

export default function Certs() {
  // Ключ файла — в строке запроса, а не сегментом пути: это путь в бакете, в нём косые
  // черты, кириллица и пробелы (см. ПУТИ в lib/certs.ts).
  const [параметры] = useSearchParams()
  const ключ = параметры.get('key')
  return ключ ? <Карточка ключФайла={ключ} /> : <Список />
}

// ═══════════════════════════════ список ═══════════════════════════════

function Список() {
  const [модель, setМодель] = useState('')
  const [номер, setНомер] = useState('')
  const [внутри, setВнутри] = useState(false)
  const [вид, setВид] = useState('')
  const [год, setГод] = useState('')
  const [папка, setПапка] = useState('')
  const [проблема, setПроблема] = useState('')
  const [сорт, setСорт] = useState('year')
  const [старт, setСтарт] = useState(0)

  const refs = useСертыRefs()
  const счёт = refs.data?.counts
  const заметки = refs.data?.data_notes ?? {}

  const фильтры = useMemo<ФильтрыСертов>(() => ({
    model: модель.trim() || undefined,
    serial: номер.trim() || undefined,
    deep: внутри || undefined,
    registr: вид || undefined,
    year: год || undefined,
    folder: папка || undefined,
    problem: проблема || undefined,
    sort: сорт,
    start: старт,
    limit: РАЗМЕР_СТРАНИЦЫ,
  }), [модель, номер, внутри, вид, год, папка, проблема, сорт, старт])

  const запрос = useСертификаты(фильтры)
  const данные = запрос.data
  const сСброса = <T,>(setter: (v: T) => void) => (v: T) => { setСтарт(0); setter(v) }
  const искали = !!(модель.trim() || номер.trim())

  return (
    <>
      <div className="page-head">
        <h1>Сертификаты РМРС/РКО</h1>
        <span className="muted">
          {данные ? `найдено: ${данные.total}` : ''}
          {счёт ? ` · в картотеке: ${pluralN(счёт.docs, 'файл', 'файла', 'файлов')},`
            + ` ${pluralN(счёт.serials_unique, 'заводской номер', 'заводских номера',
              'заводских номеров')}` : ''}
        </span>
      </div>

      <Охват scope={данные?._scope} />

      <div className="notice">
        <b>Это картотека файлов, а не реестр действующих свидетельств.</b>{' '}
        {заметки.valid_to
          ?? 'Срока действия в данных нет ни у одного файла: раздел отвечает «такой сертификат'
             + ' есть», а не «он действует».'}
        <div className="hint" style={{ marginTop: 6 }}>
          {заметки.not_all
            ?? 'Здесь только сертификаты, выложенные синком портала в R2. Портал 55 ищет ещё и'
               + ' по своему старому набору — не найдя нужного здесь, проверьте на портале.'}
          {' '}{заметки.serials ?? ''}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <FilterBar id="certs">
          <div className="f" style={{ flex: 2, minWidth: 240 }}>
            <label htmlFor="cmodel">Модель оборудования или имя файла</label>
            <input id="cmodel" value={модель}
                   onChange={(e) => сСброса(setМодель)(e.target.value)}
                   placeholder="например: FELCOM, CDC2412, YAR27" />
          </div>

          <div className="f" style={{ flex: 2, minWidth: 220 }}>
            <label htmlFor="cserial">Заводской (серийный) номер</label>
            <input id="cserial" value={номер}
                   onChange={(e) => сСброса(setНомер)(e.target.value)}
                   placeholder="например: 15306002 или 4508-5581" />
          </div>

          <div className="f" style={{ minWidth: 200 }}>
            <label htmlFor="cdeep">Как искать</label>
            <select id="cdeep" value={внутри ? '1' : '0'}
                    onChange={(e) => сСброса(setВнутри)(e.target.value === '1')}>
              <option value="0">точно: с начала модели, номер целиком</option>
              <option value="1">внутри строки и внутри номера</option>
            </select>
          </div>

          <div className="f" style={{ minWidth: 150 }}>
            <label htmlFor="creg">Вид</label>
            <select id="creg" value={вид} onChange={(e) => сСброса(setВид)(e.target.value)}>
              <option value="">любой</option>
              {(refs.data?.by_registr ?? []).map((в) => (
                <option key={String(в.k)}
                        value={в.k === '(не определён)' ? 'нет' : String(в.k)}>
                  {String(в.k)} ({в.n})
                </option>
              ))}
            </select>
          </div>

          <div className="f" style={{ minWidth: 140 }}>
            <label htmlFor="cyear">Год документа</label>
            <select id="cyear" value={год} onChange={(e) => сСброса(setГод)(e.target.value)}>
              <option value="">любой</option>
              {(refs.data?.by_year ?? []).map((г) => (
                <option key={String(г.k)} value={г.k === null ? 'нет' : String(г.k)}>
                  {г.k === null ? 'года нет в данных' : String(г.k)} ({г.n})
                </option>
              ))}
            </select>
          </div>

          <div className="f" style={{ minWidth: 190 }}>
            <label htmlFor="cfolder">Папка</label>
            <select id="cfolder" value={папка} onChange={(e) => сСброса(setПапка)(e.target.value)}>
              <option value="">любая</option>
              {(refs.data?.by_folder ?? []).map((п) => (
                <option key={String(п.k)} value={String(п.k)}>
                  {String(п.k) || '(корень)'} ({п.n})
                </option>
              ))}
            </select>
          </div>

          <div className="f" style={{ minWidth: 210 }}>
            <label htmlFor="cprob">Что неполно</label>
            <select id="cprob" value={проблема}
                    onChange={(e) => сСброса(setПроблема)(e.target.value)}>
              <option value="">показывать все</option>
              {(refs.data?.problems ?? []).map((п) => (
                <option key={String(п.k)} value={String(п.k)}
                        title={подписьПроблемы(String(п.k)).смысл}>
                  {подписьПроблемы(String(п.k)).ярлык} ({п.n})
                </option>
              ))}
            </select>
          </div>

          <div className="f" style={{ minWidth: 180 }}>
            <label htmlFor="csort">Сортировка</label>
            <select id="csort" value={сорт} onChange={(e) => сСброса(setСорт)(e.target.value)}>
              <option value="year">год: сначала свежие</option>
              <option value="year_asc">год: сначала старые</option>
              <option value="model">по модели</option>
              <option value="serials">по числу номеров</option>
              <option value="file">по папке и имени файла</option>
            </select>
          </div>
        </FilterBar>

        <div className="hint" style={{ marginTop: 10 }}>
          {данные && искали ? <РежимПоиска д={данные} /> : (
            <>
              Поиск по <b>модели</b> идёт с начала строки и по индексу: «FELCOM» найдёт
              FELCOM-18 и FELCOM-19, а «18» — нет. Поиск по <b>номеру</b> сверяет номер целиком.
              Не нашли — переключите «как искать» на поиск внутри строки.
            </>
          )}
        </div>
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        <div className="card">
          {/* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12). */}
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th>Модель</th>
                  <th className="nowrap">Вид</th>
                  <th className="nowrap">Год</th>
                  <th>Заводские номера</th>
                  <th>Файл</th>
                </tr>
              </thead>
              <tbody>
                {(данные?.rows ?? []).map((с) => <Строка key={с.r2_key} с={с} />)}
                {данные && данные.rows.length === 0 && (
                  <tr>
                    <td colSpan={5}>
                      <Пусто модель={модель} номер={номер} внутри={внутри} refs={refs.data} />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Пейджер start={данные?.start ?? 0} total={данные?.total ?? 0}
                   next={данные?.next ?? null} busy={запрос.isFetching} onStart={setСтарт} />
          <Перенос refs={refs.data} />
        </div>
      </QueryState>
    </>
  )
}

/** Как именно искали. Молчать об этом нельзя: пустой ответ иначе читается как «нет такого». */
function РежимПоиска({ д }: { д: { search_model: string; search_serial: string } }) {
  const части = [
    д.search_model !== 'нет' ? `по модели — ${ПОЯСНЕНИЕ_РЕЖИМА[д.search_model] ?? д.search_model}` : '',
    д.search_serial !== 'нет' ? `по номеру — ${ПОЯСНЕНИЕ_РЕЖИМА[д.search_serial] ?? д.search_serial}` : '',
  ].filter(Boolean)
  return <>Искали: {части.join('; ')}.</>
}

function Пусто({ модель, номер, внутри, refs }: {
  модель: string
  номер: string
  внутри: boolean
  refs: СправочникиСертов | undefined
}) {
  const непокрыто = refs?.last_import?.notes?.not_covered?.portal_nextcloud_certs
  return (
    <div className="empty">
      <div className="empty-title">В картотеке CRM такого сертификата нет</div>
      <div className="empty-hint">
        {!внутри && (модель || номер) ? (
          <>
            Искали точно: модель — с начала строки, номер — целиком. Переключите «как искать»
            на поиск внутри строки: в свидетельстве номер записан «4508-5581», а на наклейке
            прибора читается «5581».
          </>
        ) : (
          <>Модель в свидетельстве может быть записана иначе, чем в прайсе: «FT-3500R Series»
            вместо «FT-3507R».</>
        )}
        <div style={{ marginTop: 8 }}>
          <b>Это не значит, что сертификата не существует.</b> Портал 55 ищет ещё и по своему
          старому набору из Nextcloud
          {непокрыто ? ` (${pluralN(непокрыто, 'файл', 'файла', 'файлов')})` : ''}, которого в
          CRM пока нет. Проверьте там, прежде чем сказать судовладельцу «нет».
        </div>
      </div>
    </div>
  )
}

function Строка({ с }: { с: Сертификат }) {
  const вид = видСвидетельства(с.registr)
  const номера = с.serials ?? []
  const совпало = с.serial_matched ?? []
  const проблемы = с.problems ?? []
  return (
    <tr>
      <td data-label="Модель">
        <Link to={ПУТИ.сертификат(с.r2_key)}>{fmtText(с.model)}</Link>
        {проблемы.length > 0 && (
          <div className="hint">
            {проблемы.map((к) => (
              <span key={к} className="badge work" title={подписьПроблемы(к).смысл}
                    style={{ marginRight: 4 }}>
                {подписьПроблемы(к).ярлык}
              </span>
            ))}
          </div>
        )}
        {с.extract_source === 'manual' && (
          <div className="hint">поправлено человеком — перенос это не затирает</div>
        )}
      </td>
      <td className="nowrap" data-label="Вид">
        <span className={классВида(с.registr)}>{вид.ярлык}</span>
      </td>
      <td className="nowrap num" data-label="Год">
        {с.year ?? <span className="muted">нет в данных</span>}
      </td>
      <td data-label="Заводские номера">
        {номера.length === 0 ? (
          <span className="muted">
            номеров в свидетельстве нет — конкретный прибор им не подтвердить
          </span>
        ) : (
          <>
            {/* Совпавшие номера — первыми и значком: человек искал именно их. */}
            {совпало.map((s) => (
              <span key={s} className="badge won mono" style={{ marginRight: 4 }}>{s}</span>
            ))}
            <span className="mono">
              {номера.filter((s) => !совпало.includes(s)).slice(0, 8).join(', ')}
            </span>
            <div className="hint">
              всего номеров: {с.serial_count ?? номера.length}
              {номера.length > 8 + совпало.length ? ' — остальные в карточке файла' : ''}
            </div>
          </>
        )}
      </td>
      <td data-label="Файл">
        {/* rel="noreferrer": ссылка уходит на другой домен (портал 55), и передавать ему
            адрес страницы CRM незачем. */}
        <a href={с.file_url} target="_blank" rel="noreferrer">PDF</a>
        <div className="hint">
          {fmtText(с.file_name)} · {размерФайла(с.size_bytes)}
        </div>
        {серьёзнаяНеполнота(с.problems) && (
          <div className="hint">смотрите сам документ, а не только эту строку</div>
        )}
      </td>
    </tr>
  )
}

/** Когда переносили картотеку и что в ней неполно. Свежесть здесь — свойство ПЕРЕНОСА. */
function Перенос({ refs }: { refs: СправочникиСертов | undefined }) {
  const п = refs?.last_import
  if (!refs) return null
  if (!п) {
    return (
      <div className="hint">
        Отметки о переносе картотеки нет: раздел показывает то, что есть в базе, но сказать,
        насколько это свежо, нечем.
      </div>
    )
  }
  const непокрыто = п.notes?.not_covered
  return (
    <div className="hint">
      Картотека перенесена {fmtDate(п.finished_at ?? п.started_at, true)} из файла{' '}
      <span className="mono">{fmtText(п.src_file)}</span> (правлен{' '}
      {fmtDate(п.src_mtime)}):{' '}
      {п.files_seen === null || п.files_seen === undefined
        ? '— файлов' : pluralN(п.files_seen, 'файл', 'файла', 'файлов')},{' '}
      {п.serials_total === null || п.serials_total === undefined
        ? '— номеров' : pluralN(п.serials_total, 'номер', 'номера', 'номеров')}.
      {' '}Живого синка у раздела нет — новые свидетельства попадают сюда отдельным прогоном,
      а не сами.
      {непокрыто?.portal_nextcloud_certs
        ? ` Ещё ${pluralN(непокрыто.portal_nextcloud_certs,
          'сертификат', 'сертификата', 'сертификатов')} портала`
          + ` ${plural(непокрыто.portal_nextcloud_certs, 'лежит', 'лежат', 'лежат')}`
          + ' в Nextcloud без ключа R2 — их CRM не ищет.'
        : ''}
      {refs.file_needs_portal_login
        ? ' PDF отдаёт портал 55: без входа в портал ссылка ответит «Unauthorized» —'
          + ' это не значит, что файла нет.'
        : ''}
    </div>
  )
}

// ═══════════════════════════════ карточка ═══════════════════════════════

function Карточка({ ключФайла }: { ключФайла: string }) {
  const запрос = useСертификат(ключФайла)
  const с = запрос.data

  return (
    <>
      <div className="page-head">
        <h1>{с?.model || 'Сертификат'}</h1>
        <div className="spacer" />
        <Link to={ПУТИ.список}>← к картотеке</Link>
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        {!с ? (
          <div className="card">
            <div className="empty">
              <div className="empty-title">Такого файла в картотеке нет</div>
              <div className="empty-hint">
                Ссылка могла устареть: файл переложили в папке, и ключ изменился.
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="card" style={{ marginBottom: 16 }}>
              <dl className="dl">
                <dt>Модель</dt>
                <dd>{fmtText(с.model)}</dd>
                <dt>Вид</dt>
                <dd>
                  <span className={классВида(с.registr)}>{видСвидетельства(с.registr).ярлык}</span>
                </dd>
                <dt>Год документа</dt>
                <dd>
                  {с.year ?? <span className="muted">нет в данных</span>}
                  <div className="hint">
                    {с.data_notes?.year ?? 'Это год документа, а не срок действия.'}
                  </div>
                </dd>
                <dt>Файл</dt>
                <dd>
                  <a href={с.file_url} target="_blank" rel="noreferrer">
                    {fmtText(с.file_name)}
                  </a>
                  <div className="hint">
                    {размерФайла(с.size_bytes)} · папка{' '}
                    <span className="mono">{fmtText(с.folder)}</span>
                    <div>{с.data_notes?.file ?? ''}</div>
                  </div>
                </dd>
                <dt>Срок действия</dt>
                <dd>
                  <span className="muted">в данных нет</span>
                  <div className="hint">{с.data_notes?.valid_to ?? ''}</div>
                </dd>
                <dt>Откуда данные</dt>
                <dd>
                  {с.extract_source === 'manual'
                    ? 'поправлено человеком — перенос эту запись не затирает'
                    : 'вычитка PDF языковой моделью при синке портала'}
                  <div className="hint">
                    Ключ в R2: <span className="mono">{с.r2_key}</span>
                    {с.etag ? '' : ' · контрольной суммы файла в данных нет'}
                  </div>
                </dd>
              </dl>

              {(с.problems ?? []).length > 0 && (
                <div className="notice" style={{ marginTop: 12 }}>
                  <b>Что в этой записи неполно:</b>
                  <ul className="plain-list">
                    {(с.problems ?? []).map((к) => (
                      <li key={к}>
                        <span className="badge work">{подписьПроблемы(к).ярлык}</span>{' '}
                        {подписьПроблемы(к).смысл}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <Номера с={с} />
            <Соседи подпись="Другие документы на ту же модель" строки={с.same_model}
                    пусто="других документов на эту модель в картотеке нет"
                    всего={с.same_model_total} скрыто={с.same_model_hidden}
                    предел={с.neighbours_limit} />
            <ОбщиеНомера с={с} />
            <Реестр с={с} />
          </>
        )}
      </QueryState>
    </>
  )
}

function Номера({ с }: { с: КарточкаСертификата }) {
  const номера = с.serials ?? []
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h2>Заводские номера ({с.serial_count ?? номера.length})</h2>
      {номера.length === 0 ? (
        <div className="empty">
          <div className="empty-title">Номеров в свидетельстве нет</div>
          <div className="empty-hint">
            Либо это одобрение типа на всю модель, либо номера не удалось прочитать. Подтвердить
            им конкретный прибор нельзя — откройте PDF.
          </div>
        </div>
      ) : (
        <>
          <div className="mono" style={{ lineHeight: 1.8 }}>
            {номера.map((s, i) => (
              <span key={`${s}-${i}`} className="badge" style={{ marginRight: 6 }}>{s}</span>
            ))}
          </div>
          <div className="hint" style={{ marginTop: 8 }}>
            {с.data_notes?.serials ?? ''}
          </div>
        </>
      )}
    </div>
  )
}

function Соседи({ подпись, строки, пусто, всего, скрыто, предел }: {
  подпись: string
  строки: Сертификат[]
  пусто: string
  /** Сколько таких документов ЕСТЬ. Может отличаться от длины списка: карточка обрезает. */
  всего?: number
  скрыто?: number
  предел?: number
}) {
  // В заголовке — «показано N из M», а не голое N. Обрезка, о которой не сказали, читается
  // как полный список: человек видит двадцать пять соседей и решает, что их двадцать пять.
  const обрезано = (скрыто ?? 0) > 0
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h2>{подпись} ({обрезано ? `${строки.length} из ${всего}` : строки.length})</h2>
      {обрезано && (
        <div className="hint">
          Показаны{' '}
          {pluralN(строки.length, 'первый документ', 'первых документа', 'первых документов')}
          {' '}из {всего}: карточка обрезает список на {предел ?? строки.length}. Остальные{' '}
          {скрыто} ищите в списке через фильтр по модели — здесь их нет, и «других нет» это
          не значит.
        </div>
      )}
      {строки.length === 0 ? (
        <div className="empty">
          <div className="empty-title">{пусто}</div>
        </div>
      ) : (
        <div className="table-wrap cards">
          <table>
            <thead>
              <tr>
                <th>Модель</th>
                <th className="nowrap">Вид</th>
                <th className="nowrap">Год</th>
                <th className="nowrap">Номеров</th>
                <th>Файл</th>
              </tr>
            </thead>
            <tbody>
              {строки.map((с) => (
                <tr key={с.r2_key}>
                  <td data-label="Модель">
                    <Link to={ПУТИ.сертификат(с.r2_key)}>{fmtText(с.model)}</Link>
                  </td>
                  <td className="nowrap" data-label="Вид">
                    <span className={классВида(с.registr)}>{видСвидетельства(с.registr).ярлык}</span>
                  </td>
                  <td className="nowrap num" data-label="Год">{с.year ?? '—'}</td>
                  <td className="nowrap num" data-label="Номеров">{с.serial_count ?? 0}</td>
                  <td data-label="Файл">
                    <a href={с.file_url} target="_blank" rel="noreferrer">PDF</a>
                    <div className="hint">{fmtText(с.file_name)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

/** Документы, где встречается тот же заводской номер: на один прибор бывает и РМРС, и РКО. */
function ОбщиеНомера({ с }: { с: КарточкаСертификата }) {
  const строки = с.same_serials ?? []
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h2>Те же заводские номера в других документах ({строки.length})</h2>
      {строки.length === 0 ? (
        <div className="empty">
          <div className="empty-title">
            Номера этого свидетельства в других файлах картотеки не встречаются.
          </div>
        </div>
      ) : (
        <div className="table-wrap cards">
          <table>
            <thead>
              <tr>
                <th>Модель</th>
                <th className="nowrap">Вид</th>
                <th className="nowrap">Год</th>
                <th>Совпавшие номера</th>
                <th>Файл</th>
              </tr>
            </thead>
            <tbody>
              {строки.map((р) => (
                <tr key={р.r2_key}>
                  <td data-label="Модель">
                    <Link to={ПУТИ.сертификат(р.r2_key)}>{fmtText(р.model)}</Link>
                  </td>
                  <td className="nowrap" data-label="Вид">
                    <span className={классВида(р.registr)}>{видСвидетельства(р.registr).ярлык}</span>
                  </td>
                  <td className="nowrap num" data-label="Год">{р.year ?? '—'}</td>
                  <td className="mono" data-label="Совпавшие номера">
                    {(р.shared ?? []).slice(0, 8).join(', ')}
                  </td>
                  <td data-label="Файл">
                    <a href={р.file_url} target="_blank" rel="noreferrer">PDF</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

/**
 * След в реестре РМРС. ЭТО ПОДСКАЗКА, А НЕ ПОДТВЕРЖДЕНИЕ, и написано это прямо, а не мелким
 * шрифтом: совпадение ищется подстрокой в названии изделия, потому что иначе в реестре не
 * найти ничего (там все названия начинаются с номера пункта свидетельства). Ядро помечает
 * рваные границы — «UPS-10» так находится внутри «BOLID UPS-1000-SD», и это другой прибор.
 */
function Реестр({ с }: { с: КарточкаСертификата }) {
  const строки = с.registry ?? []
  const чистые = строки.filter((р) => р.tight)
  const рваные = строки.filter((р) => !р.tight)
  return (
    <div className="card">
      <h2>Похожее в реестре РМРС ({строки.length})</h2>
      {!с.registry_searched ? (
        <div className="hint">
          В реестре не искали: {с.registry_why_not || 'по этой модели поиск дал бы случайные'
            + ' совпадения'}.
        </div>
      ) : строки.length === 0 ? (
        <div className="empty">
          <div className="empty-title">
            Изделия с таким обозначением во всём реестре РМРС не нашлось.
          </div>
          <div className="empty-hint">
            Это не значит, что одобрения нет: Регистр называет изделия своими словами — «радар» у
            него «радиолокационная станция», и обозначение модели в названии бывает не указано.
          </div>
        </div>
      ) : (
        <>
          <div className="notice">
            <b>Это сопоставление по названию, а не подтверждение.</b>{' '}
            {с.data_notes?.registry
              ?? 'Совпадение ищется подстрокой в названии изделия: «UPS-10» так находит'
                 + ' «BOLID UPS-1000-SD».'}{' '}
            Прежде чем ссылаться на строку реестра, сверьте изготовителя и номер свидетельства.
          </div>
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th>Изделие в реестре</th>
                  <th>Изготовитель</th>
                  <th className="nowrap">№ свидетельства</th>
                  <th className="nowrap">Действует до</th>
                  <th className="nowrap">Совпадение</th>
                </tr>
              </thead>
              <tbody>
                {[...чистые, ...рваные].map((р) => (
                  <СтрокаРеестра key={р.src_id} р={р} />
                ))}
              </tbody>
            </table>
          </div>
          <div className="hint" style={{ marginTop: 8 }}>
            Срок действия в этой таблице — из реестра РМРС, а не из сертификата: у файлов
            картотеки срока нет вовсе.
          </div>
        </>
      )}
    </div>
  )
}

function СтрокаРеестра({ р }: { р: СледВРеестре }) {
  return (
    <tr>
      <td data-label="Изделие в реестре">
        <Link to={ПУТИ_РЕЕСТРА.изделие(р.src_id)}>{fmtText(р.product_name)}</Link>
      </td>
      <td data-label="Изготовитель">{fmtText(р.manufacturer)}</td>
      <td className="mono nowrap" data-label="№ свидетельства">{fmtText(р.certificate_no)}</td>
      <td className="nowrap" data-label="Действует до">
        {fmtDate(р.valid_to)}
        {р.expired ? <div><span className="badge lose">срок прошёл</span></div> : null}
      </td>
      <td className="nowrap" data-label="Совпадение">
        {р.tight ? (
          <span className="badge">обозначение целиком</span>
        ) : (
          <span className="badge work" title="модель нашлась внутри другого обозначения">
            внутри другого обозначения
          </span>
        )}
      </td>
    </tr>
  )
}
